import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { graphql } from 'gatsby';
import { Column, Grid } from 'components';
import { DesktopBreadcrumbs, MobileBreadcrumbs } from 'components/Breadcrumbs';
import SeoBlock from 'components/SeoBlock';
import ProductGrid from 'components/ProductGrid';
import { DeviceSize, useWindowSize } from 'hooks/useWindowSize';
import Show from 'components/Functional/Show';
import { SchemaOrgCategory } from 'components/SchemaOrg/SchemaOrgCategory';
import { CLPTemplateProps, FilterLayout } from 'types/Category';
import { CLPMetatags } from 'components/Metatags/CLPMetatags';
import { BottomCategoryBanner, CategoryHeader } from 'components/CLP';
import { usePagination } from 'components/CLP/usePagination';
import { analyticsWrapper } from 'analytics/itly/analyticsWrapper';
import { useTrackGleamEmailSubmitted, useTrackPageViewed } from 'analytics/itly/hooks';
import withCart from 'components/PageWrapper/withCart';
import { useTrackProductListViewed } from 'analytics/itly/hooks/useTrackProductListViewed';
import CLPFilters from 'components/CLPFilters';
import { useFilters } from 'components/CLP/useFilters';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilterState, updateFilterState, updatePaginationState } from 'store/ui/actions';
import { filterStateSelector, paginationStateSelector } from 'store/ui/selectors';
import { caseInsensitiveSlugMatch } from 'helpers/strings';
import { getDefaultPageLimitOptions } from 'constants/clp';
import { getMetatags } from 'helpers/clp/metatags';
import { Metatags } from '../../types/Metatags';
import { getH2Text, shouldHideProducts, shouldShowPrices } from 'helpers/clp';
import {
  DesktopClearFiltersButton,
  DesktopFilterHeader,
  DesktopFilters,
  H2Header,
  SubcategoryNameHeading
} from './styled';
import GlobalLayout from 'components/Layouts/GlobalLayout';
import { SEO } from 'components/SEO';
import { getPageCountFromUrl } from 'helpers/pagination';
import { CLPPagination } from 'components/Pagination/CLPPagination';
import { useTikTok } from 'hooks/useTikTok';
import { FAQ } from 'components/FAQ';
import {
  CHRISTMAS_FILTER_OPTION,
  CHRISTMAS_URL_PATH,
  HOLIDAY_MULTISELECT,
  NEW_YEAR_FILTER_OPTION,
  NEW_YEAR_URL_PATH
} from 'components/constants/products';
import { FilterBar } from 'components/CLP/FilterBar';
import SubcategoryGrid from 'components/SubcategoryGrid';
import { getActiveSubcategoryName } from 'components/CLPFilters/helpers';
import { isNotServerSideRendering } from 'helpers/env';
import { useFilterHandlers } from '../../hooks/useFilterHandlers';
import { redirectToPage } from 'helpers/navigation';

export const query = graphql`
  query ProductCategory($categoryId: Int) {
    productCategory(categoryId: { eq: $categoryId }) {
      ...BaseCategory
      ...CategorySideMenu
      ...Banners
      products {
        ...BaseProduct
        clpHoverImage
        clpHoverVideoId
        clpCarouselImages
        clpLink
        livePreviewAttributes
        customOptions {
          name
          filterAttribute
          use_case_option
          values {
            label
            price
            filterValue
            use_case_default_image_url
            use_case_product_title
          }
        }
        productCallout {
          callout1
          callout2
          callout3
        }
      }
      metaTitle
      metaDescription
      metaKeywords
      frequentlyAskedQuestions {
        question
        answer
      }
      availableFilters {
        attributeCode: attribute_code
        label
        options
      }
      showClpFilters
    }
    holidayCardCategory: productCategory(categoryId: { eq: 67 }) {
      ...BaseCategory
      ...CategorySideMenu
      ...Banners
      products {
        ...BaseProduct
        clpHoverImage
        clpHoverVideoId
        clpCarouselImages
        clpLink
        livePreviewAttributes
        customOptions {
          name
          filterAttribute
          values {
            label
            price
            filterValue
          }
        }
      }
      metaTitle
      metaDescription
      metaKeywords
      availableFilters {
        attributeCode: attribute_code
        label
        options
      }
    }
    allYotpoProductBottomline {
      nodes {
        score
        totalReviews
        productIdentifier
      }
    }
  }
`;

const CLPTemplate = ({ pageContext, data, tracking }: CLPTemplateProps) => {
  const category = data.productCategory;
  const categoryId = category.categoryId;
  const showClpFilters = category.showClpFilters === '1';
  const holidayCardCategory = data.holidayCardCategory;
  const isHolidayCardsCLP = categoryId === 67 || categoryId === 68 || categoryId === 65; // If holiday, christmas, or new years clp
  const isBookCLP = category.urlPath.includes('photo-books') || category.urlPath.includes('wedding/photo-albums');
  const isPhotoBookCLP = categoryId === 3;
  const { currentCanonicalCategory, breadcrumbs, baseCanonicalUrl, pagination } = pageContext;
  const pageCountFromUrl = getPageCountFromUrl();
  const isNotSSR = isNotServerSideRendering();
  const searchUrl = isNotSSR ? window.location.search : '';

  const yotpoRatings = data.allYotpoProductBottomline.nodes;
  const productSKUs = category.products.map((product) => product.sku);
  const productIDs = category.products.map((product) => product.productId);

  const productsWithReviews = yotpoRatings.filter(
    (item) => productSKUs.includes(item.productIdentifier) || productIDs.includes(parseInt(item.productIdentifier))
  );

  const averageCategoryReviewScore =
    productsWithReviews.reduce((sum, item) => sum + item.score, 0) / productsWithReviews.length;
  const categoryReviewsNumber = productsWithReviews.reduce((sum, item) => sum + item.totalReviews, 0);

  category.averageReviewScore = averageCategoryReviewScore.toFixed(5);
  category.totalReviews = categoryReviewsNumber;

  if (isHolidayCardsCLP && holidayCardCategory) {
    category.products = holidayCardCategory.products;
    category.availableFilters = holidayCardCategory.availableFilters;
  }

  const filterState = useSelector(filterStateSelector);
  const filteredCategory = useFilters(category, filterState.filters);

  const { productPages, currentPage } = usePagination(filteredCategory);
  const activeSubcategoryName = useMemo(
    () => getActiveSubcategoryName(filterState, category.subcategoryFilters),
    [filterState, category.subcategoryFilters]
  );

  const uiState = useSelector(paginationStateSelector);

  const { handleFilterClear } = useFilterHandlers(filterState.filters);

  const { deviceSize } = useWindowSize();
  const isMobile = deviceSize === DeviceSize.SMALL;
  const subcategoryColumnSize = isMobile ? 12 : 13; // Need larger column width for desktop to span product images
  const subcategoryColumnStyle = isMobile ? { paddingRight: 0 } : {};
  const dispatch = useDispatch();

  useEffect(() => {
    const currentPageHasNoResults = uiState.currentPage >= productPages.length;
    const shouldRedirectToFirstPage = uiState.currentPage > 0 && currentPageHasNoResults;
    if (shouldRedirectToFirstPage) {
      const queryParams = new URLSearchParams(window.location.search);
      const destination = queryParams.size > 0 ? `${category.urlPath}/?${queryParams.toString()}` : category.urlPath;
      redirectToPage(destination);
    }
  }, [category.urlPath, productPages.length, uiState.currentPage]);

  const paginationPageLimits = useMemo(() => {
    return getDefaultPageLimitOptions(category);
  }, [category]);

  const displayCount = pageCountFromUrl || uiState.displayCount || paginationPageLimits[0].value;

  //Hide pagination eniterly if there are less products than the lowest pagination limit
  const hidePagination = productPages[0]?.length < paginationPageLimits[0].value;

  useTrackPageViewed(tracking, 'Category Listing Page', category.name); // To track individual pages pass `currentPage` as an extra argument
  useTrackProductListViewed(tracking, breadcrumbs, category.products);
  useTrackGleamEmailSubmitted(tracking);

  useTikTok({ event: 'PageView' });

  const hasSubcategoryFilters = category.subcategoryFilters?.length > 0;

  const showDesktopSubcategories = hasSubcategoryFilters && category.showSubcategoriesOnDesktop && !isMobile;
  const showMobileSubcategories = hasSubcategoryFilters && category.showSubcategoriesOnMobile && isMobile;

  const showSubcategories = showDesktopSubcategories || showMobileSubcategories;

  const showActiveSubcategoryName = showDesktopSubcategories && activeSubcategoryName;

  const holidayFilterOptions = filterState.filters.find((f) => f.attributeCode === HOLIDAY_MULTISELECT)?.options;
  const isChristmasCLP =
    isHolidayCardsCLP && categoryId === 68 && !holidayFilterOptions?.every((o) => o === CHRISTMAS_FILTER_OPTION);

  const isNewYearCLP =
    isHolidayCardsCLP && categoryId === 65 && !holidayFilterOptions?.every((o) => o === NEW_YEAR_FILTER_OPTION);

  const isChristmasUrl = CHRISTMAS_URL_PATH === category.urlPath;
  const isNewYearUrl = NEW_YEAR_URL_PATH === category.urlPath;

  const shouldClearFilters = (isChristmasCLP, isNewYearCLP) => {
    if (isChristmasCLP && !isNewYearCLP) {
      return false;
    } else if (!isChristmasCLP && isNewYearCLP) {
      return false;
    } else {
      return true;
    }
  };

  const clearFilters = shouldClearFilters(isChristmasCLP, isNewYearCLP);

  useLayoutEffect(() => {
    if (isChristmasCLP) {
      // If category is christmas cards, auto apply christmas filter
      dispatch(
        updateFilterState({
          filters: [
            {
              label: CHRISTMAS_FILTER_OPTION,
              attributeCode: HOLIDAY_MULTISELECT,
              options: [CHRISTMAS_FILTER_OPTION]
            }
          ]
        })
      );
    } else if (isNewYearCLP) {
      // If category is new years cards, auto apply new years filter
      dispatch(
        updateFilterState({
          filters: [
            {
              label: NEW_YEAR_FILTER_OPTION,
              attributeCode: HOLIDAY_MULTISELECT,
              options: [NEW_YEAR_FILTER_OPTION]
            }
          ]
        })
      );
    }
    if (clearFilters && !isChristmasUrl && !isNewYearUrl) {
      dispatch(clearFilterState());
    }
    // TODO: Fix this dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, holidayCardCategory, categoryId]);

  useEffect(() => {
    const pageIndex = pagination?.page ? pagination.page - 1 : 0;
    dispatch(updatePaginationState(categoryId, pageIndex, displayCount));
  }, [dispatch, displayCount, categoryId, pagination?.page]);

  useEffect(() => {
    if (!currentCanonicalCategory) {
      return;
    }

    const canonicalFilterOptions = currentCanonicalCategory.meta.filters
      .split(',')
      .map((option) => option.charAt(0).toUpperCase() + option.slice(1));

    const clpDeepLinkFilters = canonicalFilterOptions.map((option) => {
      // @TODO singlephoto, nophoto should be hyphenated similiar to multi-photo
      if (option === 'Singlephoto') {
        option = 'Single Photo';
      }
      if (option === 'Nophoto') {
        option = 'No Photo';
      }

      const filterType = category.availableFilters.find((filterType) => {
        return filterType.options.some((filterTypeOption: string) =>
          caseInsensitiveSlugMatch(filterTypeOption, option)
        );
      });
      const attributeCode = filterType?.attributeCode;
      const label = filterType?.label;

      return { attributeCode, label, options: [option] };
    });

    dispatch(updateFilterState({ filters: clpDeepLinkFilters }));
  }, [dispatch, currentCanonicalCategory, category.availableFilters]);

  const isLowResults = Object.keys(filterState).length > 0 && filteredCategory.products.length <= 2;

  const metatags: Metatags = getMetatags(category, baseCanonicalUrl, currentCanonicalCategory, searchUrl, isLowResults);

  return (
    <GlobalLayout tracking={tracking} displayPromoBanner displayProductAssistant templatePageContext="DEFAULT">
      <SEO title={metatags.seoTitle} description={metatags.metaDescription} currentPage={pagination?.page} />
      <CLPMetatags currentPage={pagination?.page} {...metatags} />
      <SchemaOrgCategory category={category} />
      <Grid gutter={15}>
        <Column size={12}>
          <DesktopBreadcrumbs pages={breadcrumbs} isClp={true} />
        </Column>
        <Column size={12}>
          {showClpFilters && deviceSize === DeviceSize.SMALL ? (
            <CategoryHeader
              categoryBanner={category.categoryBanner}
              h1={metatags.h1}
              currentPage={pagination?.page}
              withStyles={{ justifyContent: 'center', textAlign: 'center' }}
            />
          ) : (
            <>
              <CategoryHeader
                categoryBanner={category.categoryBanner}
                h1={metatags.h1}
                currentPage={pagination?.page}
              />
              <H2Header>{getH2Text(category.urlPath)}</H2Header>
            </>
          )}
        </Column>
        <Column size={subcategoryColumnSize} style={subcategoryColumnStyle}>
          <Show when={showSubcategories}>
            <SubcategoryGrid
              subcategoryFilters={category.subcategoryFilters}
            />
          </Show>
        </Column>
        <Show when={deviceSize === DeviceSize.SMALL}>
          <FilterBar
            resultCount={filteredCategory.products.length}
            filters={category.availableFilters}
            category={category}
          />
        </Show>
        {showClpFilters ? (
          <>
            <Column size={12} medium={3}>
              <Show when={category.sideMenuJson && deviceSize !== DeviceSize.SMALL}>
                {category.availableFilters && (
                  <>
                    <DesktopFilters>
                      <DesktopFilterHeader>FILTERS</DesktopFilterHeader>
                      <DesktopClearFiltersButton onClick={handleFilterClear}>Clear All</DesktopClearFiltersButton>
                    </DesktopFilters>
                    <CLPFilters
                      filters={category.availableFilters}
                      category={category}
                      layout={FilterLayout.VERTICAL}
                    />
                  </>
                )}
              </Show>
            </Column>

            <Column size={12} medium={9}>
              {showActiveSubcategoryName && <SubcategoryNameHeading>{activeSubcategoryName}</SubcategoryNameHeading>}

              <ProductGrid
                isBookCLP={isBookCLP}
                isPhotoBookCLP={isPhotoBookCLP}
                products={productPages[currentPage]}
                shouldShowPrices={shouldShowPrices(category)}
                totalPages={productPages.length}
                tracking={tracking}
                ratings={yotpoRatings}
                categoryId={category.categoryId}
              />

              {!hidePagination && (
                <CLPPagination
                  totalPages={productPages.length}
                  category={category}
                  paginationPageLimits={paginationPageLimits}
                  basePath={category.urlPath}
                />
              )}
              {deviceSize !== DeviceSize.SMALL && <MobileBreadcrumbs pages={breadcrumbs} />}

              <div style={{ margin: '3rem' }} />
            </Column>
            <Column size={12} medium={12} style={{}}>
              <BottomCategoryBanner category={category} />
              <SeoBlock content={category.seoBlock} currentCanonicalCategory={pageContext.currentCanonicalCategory} />
            </Column>
          </>
        ) : (
          <>
            {/* @todo: Hidden filter hacky hack to enable live previews/deep links to work without the split*/}
            <div style={{ display: 'none' }}>
              <CLPFilters filters={category.availableFilters} category={category} />
            </div>
            <Column size={12} medium={9}>
              {!shouldHideProducts(category) && (
                <>
                  <ProductGrid
                    fourColumnGrid
                    products={productPages[currentPage]}
                    shouldShowPrices={shouldShowPrices(category)}
                    totalPages={productPages.length}
                    tracking={tracking}
                    isPhotoBookCLP={isPhotoBookCLP}
                    isBookCLP={isBookCLP}
                    ratings={yotpoRatings}
                    categoryId={category.categoryId}
                  />
                  {!hidePagination && (
                    <CLPPagination
                      totalPages={productPages.length}
                      category={category}
                      paginationPageLimits={paginationPageLimits}
                      basePath={category.urlPath}
                    />
                  )}
                  <MobileBreadcrumbs pages={breadcrumbs} />
                </>
              )}
              <BottomCategoryBanner category={category} />
              <FAQ faqContent={category.frequentlyAskedQuestions} />
              <SeoBlock content={category.seoBlock} currentCanonicalCategory={pageContext.currentCanonicalCategory} />
            </Column>
          </>
        )}
      </Grid>
    </GlobalLayout>
  );
};

export default analyticsWrapper(withCart(CLPTemplate));
